import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Shield, Users, Eye, EyeOff, Lock, AlertTriangle } from 'lucide-react';

export default function ParentVisibility() {
  const navigate = useNavigate();
  const lastUpdated = 'April 16, 2026';

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Header */}
      <header className="sticky top-0 z-40" style={{ backgroundColor: 'var(--fy-background-primary)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate(-1)} 
            className="flex items-center gap-2 transition-colors"
            style={{ color: 'var(--fy-text-secondary)' }}
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center overflow-hidden" style={{ backgroundColor: 'var(--fy-background-secondary)' }}>
              <img src="https://customer-assets.emergentagent.com/job_kindplatform/artifacts/my73uiau_file_00000000f09c720c9463ab506718d330.png" alt="LikeKind" className="w-8 h-8 object-cover" />
            </div>
            <span className="font-bold" style={{ color: 'var(--fy-text-primary)' }}>LikeKind</span>
          </div>
          <div className="w-20"></div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10">
        {/* Title */}
        <div className="flex items-center gap-3 mb-2">
          <Shield className="w-8 h-8" style={{ color: 'var(--lk-sage)' }} />
          <h1 
            className="text-3xl sm:text-4xl font-bold" 
            style={{ color: 'var(--fy-text-primary)', fontFamily: 'Outfit' }}
          >
            Parent Visibility & Child Safety
          </h1>
        </div>
        <p className="mb-10" style={{ color: 'var(--fy-text-disabled)' }}>
          Last updated: {lastUpdated}
        </p>

        {/* Content */}
        <div className="space-y-10">
          
          {/* Intro */}
          <section>
            <p className="text-lg leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              LikeKind is designed to be a child's first account — structured, supervised, and calm.
            </p>
            <p className="mt-3 leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              This page explains how parent visibility works and what protections exist for children and teens.
            </p>
            <p className="mt-3 leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              LikeKind is structured around real-world activities and participation, not social profiles or content systems.
            </p>
          </section>

          {/* Why Parent Visibility Exists */}
          <section>
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
              Why Parent Visibility Exists
            </h2>
            <p className="leading-relaxed mb-4" style={{ color: 'var(--fy-text-secondary)' }}>
              Children should not navigate digital environments alone.
            </p>
            <p className="leading-relaxed mb-4" style={{ color: 'var(--fy-text-secondary)' }}>
              LikeKind is built around the principle that:
            </p>
            <ul className="list-disc list-inside space-y-2" style={{ color: 'var(--fy-text-secondary)' }}>
              <li>Protection comes from structure.</li>
              <li>Oversight is not punishment.</li>
              <li>Visibility supports safety, not surveillance.</li>
            </ul>
            <p className="mt-4 leading-relaxed font-medium" style={{ color: 'var(--fy-text-primary)' }}>
              Parent visibility is part of the architecture — not an optional add-on.
            </p>
          </section>

          {/* Account Roles Overview */}
          <section>
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
              Account Roles Overview
            </h2>
            <p className="leading-relaxed mb-6" style={{ color: 'var(--fy-text-secondary)' }}>
              LikeKind uses age-based roles that determine access and visibility.
            </p>

            {/* Child */}
            <div className="p-4 rounded-lg mb-4" style={{ backgroundColor: 'var(--fy-background-secondary)', border: '1px solid var(--fy-border-subtle)' }}>
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--fy-text-primary)' }}>
                <Lock className="w-5 h-5" style={{ color: 'var(--lk-sage)' }} />
                Child (Under 13)
              </h3>
              <ul className="list-disc list-inside space-y-1" style={{ color: 'var(--fy-text-secondary)' }}>
                <li>No public discoverability</li>
                <li>No child-to-child messaging (cannot be enabled)</li>
                <li>Parent visibility always ON</li>
                <li>Certain uploads require parent approval</li>
              </ul>
              <p className="mt-3 text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>
                Children cannot create independent accounts.
              </p>
            </div>

            {/* Teen */}
            <div className="p-4 rounded-lg mb-4" style={{ backgroundColor: 'var(--fy-background-secondary)', border: '1px solid var(--fy-border-subtle)' }}>
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--fy-text-primary)' }}>
                <Eye className="w-5 h-5" style={{ color: 'var(--lk-sage)' }} />
                Teen (Ages 13–15)
              </h3>
              <ul className="list-disc list-inside space-y-1" style={{ color: 'var(--fy-text-secondary)' }}>
                <li>Limited discoverability</li>
                <li>Parent visibility defaults ON</li>
                <li>Parent and teen may use SHARED visibility mode</li>
                <li>Teen cannot override parent visibility settings</li>
                <li>Child-to-child direct messaging remains disabled</li>
              </ul>
            </div>

            {/* Youth Adult */}
            <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--fy-background-secondary)', border: '1px solid var(--fy-border-subtle)' }}>
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--fy-text-primary)' }}>
                <Users className="w-5 h-5" style={{ color: 'var(--lk-sage)' }} />
                Youth Adult (Ages 16–17)
              </h3>
              <ul className="list-disc list-inside space-y-1" style={{ color: 'var(--fy-text-secondary)' }}>
                <li>Broader feature access</li>
                <li>Parent visibility may be ON, SHARED, or OFF depending on settings</li>
                <li>Conversion to adult account requires parent approval (if under 18)</li>
                <li>Safety rules still apply</li>
              </ul>
            </div>
          </section>

          {/* Control Modes Explained */}
          <section>
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
              Control Modes Explained
            </h2>
            <p className="leading-relaxed mb-4" style={{ color: 'var(--fy-text-secondary)' }}>
              Parent visibility operates through structured control modes:
            </p>

            <div className="space-y-4">
              <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--lk-sage-light)', border: '2px solid var(--lk-sage)' }}>
                <h3 className="font-bold mb-2" style={{ color: 'var(--fy-text-primary)' }}>ON</h3>
                <p style={{ color: 'var(--fy-text-secondary)' }}>
                  Full parent visibility. Mandatory for children under 13 and cannot be turned off.
                </p>
              </div>

              <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--fy-background-secondary)', border: '1px solid var(--fy-border-subtle)' }}>
                <h3 className="font-bold mb-2" style={{ color: 'var(--fy-text-primary)' }}>SHARED</h3>
                <p style={{ color: 'var(--fy-text-secondary)' }}>
                  Parent and minor both see activity context. Available for teens and youth adults.
                </p>
              </div>

              <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--fy-background-secondary)', border: '1px solid var(--fy-border-subtle)' }}>
                <h3 className="font-bold mb-2" style={{ color: 'var(--fy-text-primary)' }}>OFF</h3>
                <p style={{ color: 'var(--fy-text-secondary)' }}>
                  Reduced parent visibility. Available only for youth adults where enabled. Never available for accounts under 16.
                </p>
              </div>
            </div>

            <p className="mt-4 leading-relaxed font-medium" style={{ color: 'var(--fy-text-primary)' }}>
              Children are always aware when parent visibility is active.
            </p>
            <p className="mt-3 leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              Parents may see their child's activity participation and coordination status based on selected visibility settings.
            </p>
          </section>

          {/* How Child Accounts Work */}
          <section>
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
              How Child Accounts Work
            </h2>
            <p className="leading-relaxed mb-4" style={{ color: 'var(--fy-text-secondary)' }}>
              Child accounts are structured for safety:
            </p>
            <ul className="list-disc list-inside space-y-2" style={{ color: 'var(--fy-text-secondary)' }}>
              <li>Accounts are created by parents, not independently</li>
              <li>Children are not discoverable by strangers</li>
              <li>Direct messaging between children is not available</li>
              <li>Parent visibility is always active (under 13)</li>
              <li>Activity participation respects parent involvement settings</li>
            </ul>
            <p className="mt-4 leading-relaxed font-medium" style={{ color: 'var(--fy-text-primary)' }}>
              These protections are built into the system and cannot be bypassed.
            </p>
          </section>

          {/* School & Organizational Features */}
          <section>
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
              School & Organizational Features
            </h2>
            <p className="leading-relaxed mb-3" style={{ color: 'var(--fy-text-secondary)' }}>
              If LikeKind is used by a school or organization:
            </p>
            <ul className="list-disc list-inside space-y-2" style={{ color: 'var(--fy-text-secondary)' }}>
              <li>Coordination tools are structured environments, not public profiles.</li>
              <li>Communication features are limited and structured for coordination, not open conversation.</li>
              <li>Parent visibility applies where required by the account type.</li>
            </ul>
          </section>

          {/* Data & Child Privacy */}
          <section>
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
              Data & Child Privacy
            </h2>
            <ul className="list-disc list-inside space-y-2" style={{ color: 'var(--fy-text-secondary)' }}>
              <li>No behavioral advertising for minors</li>
              <li>No engagement metrics for children</li>
              <li>No external tracking pixels</li>
              <li>No sale of child data</li>
              <li>Parents may review and request deletion of child data</li>
            </ul>
            <p className="mt-4 leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              To request data review or deletion: <a href="mailto:privacy@likekind.com" className="underline" style={{ color: 'var(--lk-sage)' }}>privacy@likekind.com</a>
            </p>
          </section>

          {/* Enforcement */}
          <section>
            <div className="p-4 rounded-lg" style={{ backgroundColor: '#FEF3C7', border: '1px solid #F59E0B' }}>
              <h2 className="text-xl font-bold mb-3 flex items-center gap-2" style={{ color: '#92400E' }}>
                <AlertTriangle className="w-5 h-5" />
                Enforcement
              </h2>
              <p className="leading-relaxed" style={{ color: '#92400E' }}>
                Child safety violations may result in immediate and permanent account removal.
              </p>
              <p className="mt-2 font-medium" style={{ color: '#92400E' }}>
                Protection takes priority over convenience.
              </p>
            </div>
          </section>

          {/* The Standard */}
          <section>
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
              The Standard
            </h2>
            <p className="leading-relaxed mb-3" style={{ color: 'var(--fy-text-secondary)' }}>
              LikeKind does not promise to eliminate risk. It is designed to reduce it through structure.
            </p>
            <p className="leading-relaxed mb-3" style={{ color: 'var(--fy-text-secondary)' }}>
              Visibility exists so that children are not alone in digital spaces.
            </p>
            <p className="leading-relaxed font-medium" style={{ color: 'var(--fy-text-primary)' }}>
              Trust is not claimed. It is built through constraints.
            </p>
          </section>

          {/* Cross-link to Founder Statement */}
          <section className="pt-6" style={{ borderTop: '1px solid var(--fy-border-subtle)' }}>
            <p className="leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              To understand why LikeKind is built this way, read the{' '}
              <Link to="/founder-statement" className="underline font-medium" style={{ color: 'var(--lk-sage)' }}>
                Founder Transparency Statement
              </Link>.
            </p>
          </section>

          {/* Related Links */}
          <section className="pt-6" style={{ borderTop: '1px solid var(--fy-border-subtle)' }}>
            <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
              Related
            </h3>
            <div className="flex flex-wrap gap-3">
              <Link 
                to="/privacy" 
                className="px-4 py-2 rounded-full text-sm transition-colors"
                style={{ backgroundColor: 'var(--fy-background-secondary)', color: 'var(--fy-text-primary)', border: '1px solid var(--fy-border-subtle)' }}
              >
                Privacy Policy
              </Link>
              <Link 
                to="/terms" 
                className="px-4 py-2 rounded-full text-sm transition-colors"
                style={{ backgroundColor: 'var(--fy-background-secondary)', color: 'var(--fy-text-primary)', border: '1px solid var(--fy-border-subtle)' }}
              >
                Terms of Service
              </Link>
              <Link 
                to="/trust-safety" 
                className="px-4 py-2 rounded-full text-sm transition-colors"
                style={{ backgroundColor: 'var(--fy-background-secondary)', color: 'var(--fy-text-primary)', border: '1px solid var(--fy-border-subtle)' }}
              >
                Trust & Safety
              </Link>
            </div>
          </section>

        </div>
      </main>

      {/* Footer */}
      <footer className="mt-16 py-8" style={{ borderTop: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p style={{ color: 'var(--fy-text-disabled)' }}>
            © {new Date().getFullYear()} LikeKind Inc. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
